// composables/useCtaNavigation.ts
import type { Link } from '~/types';

export function useCtaNavigation(
  url: MaybeRefOrGetter<Link | undefined>,
  emit: (event: 'click', e: MouseEvent) => void
) {
  const attrs = useAttrs();

  const href = computed(() => toValue(url)?.href);

  const isExternal = computed(
    () =>
      toValue(url)?.external ||
      (href.value ? /^(https?:|mailto:)/.test(href.value) : false)
  );

  const isMailto = computed(() => href.value?.startsWith('mailto:') ?? false);

  const linkTarget = computed(
    () =>
      (attrs.target as string | undefined) ??
      (isExternal.value && !isMailto.value ? '_blank' : undefined)
  );

  const linkRel = computed(() =>
    isExternal.value
      ? ((attrs.rel as string | undefined) ?? 'noopener noreferrer')
      : (attrs.rel as string | undefined)
  );

  const resolvedButtonType = computed(() =>
    href.value ? undefined : getButtonType(attrs.type)
  );

  async function handleClick(event: MouseEvent) {
    emit('click', event);

    if (
      !href.value ||
      event.defaultPrevented ||
      !shouldUseClientNavigation(event)
    ) {
      return;
    }

    event.preventDefault();
    try {
      await navigateTo(href.value);
    } catch (error) {
      console.error('[useCtaNavigation] Navigation failed:', error);
    }
  }

  function shouldUseClientNavigation(event: MouseEvent) {
    return (
      !isExternal.value &&
      !hasNavigationModifier(event) &&
      attrs.target !== '_blank'
    );
  }

  function hasNavigationModifier(event: MouseEvent) {
    return (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    );
  }

  return {
    attrs,
    href,
    isExternal,
    linkTarget,
    linkRel,
    resolvedButtonType,
    handleClick,
  };
}
